import { ADS_CAMPAIGN_STATUS_DRAFT } from "../../../utils/constants";
import { getDateBasedLabel } from "../../../services/adsCampaigns";
import { aggregatePortfolioTimeSeries } from "./aggregatePortfolioTimeSeries";
import { groupPortfolioTimeSeriesByBreakdown } from "./groupPortfolioTimeSeriesByBreakdown";
import { getPrimarySpendCurrency } from "./getPrimarySpendCurrency";
import {
  getGlobalReportPaletteColor,
  getGlobalReportPlatformColor,
  GLOBAL_REPORT_BAR_COLOR,
} from "./globalReportChartPalette";

const RADAR_METRICS = [
  { key: "impressions", labelKey: "impressions" },
  { key: "ctr", labelKey: "ctr" },
  { key: "spend", labelKey: "spend" },
  { key: "mainAction", labelKey: "mainAction" },
  { key: "cpa", labelKey: "cpa", invert: true },
];

const normalizeSeries = (values, { invert = false } = {}) => {
  const numeric = values.map((v) =>
    v == null || !Number.isFinite(Number(v)) ? null : Number(v)
  );
  const valid = numeric.filter((v) => v != null);

  if (!valid.length) {
    return numeric.map(() => 0);
  }

  const min = Math.min(...valid);
  const max = Math.max(...valid);

  if (min === max) {
    const base = max > 0 ? 100 : 0;
    return numeric.map((v) => (v == null ? 0 : base));
  }

  return numeric.map((v) => {
    if (v == null) return 0;
    let score = (v - min) / (max - min);
    if (invert) score = 1 - score;
    return Math.round(score * 100);
  });
};

const sumSpendFromMap = (spendMap = {}, currency) => {
  if (!currency) {
    return Object.values(spendMap).reduce((sum, v) => sum + Number(v || 0), 0);
  }
  return Number(spendMap[currency] || 0);
};

const hasPositiveNumber = (value) =>
  value != null && Number.isFinite(Number(value)) && Number(value) > 0;

const campaignHasChartActivity = (metrics = {}) =>
  hasPositiveNumber(metrics.impressions) ||
  hasPositiveNumber(metrics.clicks) ||
  hasPositiveNumber(metrics.spend) ||
  hasPositiveNumber(metrics.mainAction);

const getGroupedPortfolioTimeSeries = ({
  campaigns,
  statistics,
  dispatch,
  breakdown = "daily",
}) => {
  const { portfolioBuckets, buckets } = aggregatePortfolioTimeSeries({
    campaigns,
    statistics,
    dispatch,
  });

  return groupPortfolioTimeSeriesByBreakdown({
    portfolioBuckets,
    buckets,
    breakdown,
  });
};

const getCampaignDisplayName = (campaign = {}) =>
  campaign?.platformsData?.[campaign?.platforms?.[0]]?.name ||
  campaign?.name ||
  "";

const aggregateCampaignMetrics = (unifiedCampaign) => {
  let impressions = 0;
  let clicks = 0;
  let mainAction = 0;
  const spendByCurrency = {};

  (unifiedCampaign?.platforms || []).forEach((platform) => {
    const metrics =
      unifiedCampaign?.levels?.campaign?.maximum?.[platform]?.campaign;
    if (!metrics) return;

    impressions += Number(metrics.impressions || 0);
    clicks += Number(metrics.click || 0);
    mainAction += Number(metrics.mainAction || 0);

    const spend = Number(metrics.spend || 0);
    const currency = metrics.currency || metrics.account_currency || "USD";
    if (spend > 0) {
      spendByCurrency[currency] =
        (Number(spendByCurrency[currency]) || 0) + spend;
    }
  });

  const primaryCurrency = getPrimarySpendCurrency(spendByCurrency);
  const spend = sumSpendFromMap(spendByCurrency, primaryCurrency);
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
  const cpa = mainAction > 0 ? spend / mainAction : null;

  return {
    impressions,
    clicks,
    mainAction,
    spend,
    spendByCurrency,
    primaryCurrency,
    ctr,
    cpa,
  };
};

export const buildGlobalReportAreaChartDataset = ({
  campaigns = {},
  statistics = null,
  dispatch,
  breakdown = "daily",
}) => {
  const { portfolioBuckets, buckets } = getGroupedPortfolioTimeSeries({
    campaigns,
    statistics,
    dispatch,
    breakdown,
  });

  if (!buckets.length) {
    return { chartData: [], chartConfig: {}, primaryCurrency: null };
  }

  const primaryCurrency = getPrimarySpendCurrency(
    portfolioBuckets.reduce((acc, bucket) => ({ ...acc, ...bucket.spend }), {})
  );

  const chartData = portfolioBuckets.map((bucket, index) => {
    const bucketMeta = buckets[index];
    const label =
      getDateBasedLabel(
        breakdown,
        bucketMeta?.startDateTime,
        bucketMeta?.endDateTime
      ) || String(index + 1);

    return {
      label,
      spend: sumSpendFromMap(bucket.spend, primaryCurrency),
      mainAction: Number(bucket.mainAction) || 0,
    };
  });

  const chartConfig = {
    spend: {
      label: "Spend",
      color: getGlobalReportPaletteColor(0),
      yAxisId: "left",
    },
    mainAction: {
      label: "Results",
      color: getGlobalReportPaletteColor(1),
      yAxisId: "right",
    },
  };

  return { chartData, chartConfig, primaryCurrency };
};

export const buildGlobalReportDailyResultsDataset = ({
  campaigns = {},
  statistics = null,
  dispatch,
  breakdown = "daily",
}) => {
  const { portfolioBuckets, buckets } = getGroupedPortfolioTimeSeries({
    campaigns,
    statistics,
    dispatch,
    breakdown,
  });

  if (!buckets.length) {
    return { chartData: [], chartConfig: {} };
  }

  const chartData = portfolioBuckets.map((bucket, index) => {
    const bucketMeta = buckets[index];
    const label =
      getDateBasedLabel(
        breakdown,
        bucketMeta?.startDateTime,
        bucketMeta?.endDateTime
      ) || String(index + 1);

    return {
      label,
      mainAction: Number(bucket.mainAction) || 0,
    };
  });

  const chartConfig = {
    mainAction: {
      label: "Results",
      color: GLOBAL_REPORT_BAR_COLOR,
    },
  };

  return { chartData, chartConfig };
};

export const buildGlobalReportImpressionsCtrAreaDataset = ({
  campaigns = {},
  statistics = null,
  dispatch,
  breakdown = "daily",
}) => {
  const { portfolioBuckets, buckets } = getGroupedPortfolioTimeSeries({
    campaigns,
    statistics,
    dispatch,
    breakdown,
  });

  if (!buckets.length) {
    return { chartData: [], chartConfig: {} };
  }

  const chartData = portfolioBuckets.map((bucket, index) => {
    const bucketMeta = buckets[index];
    const label =
      getDateBasedLabel(
        breakdown,
        bucketMeta?.startDateTime,
        bucketMeta?.endDateTime
      ) || String(index + 1);

    const impressions = Number(bucket.impressions) || 0;
    const clicks = Number(bucket.clicks) || 0;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;

    return { label, impressions, ctr };
  });

  const chartConfig = {
    impressions: {
      label: "Impressions",
      color: getGlobalReportPaletteColor(0),
      yAxisId: "left",
    },
    ctr: {
      label: "CTR",
      color: getGlobalReportPaletteColor(1),
      yAxisId: "right",
      axisValueFormat: "percent",
    },
  };

  return { chartData, chartConfig };
};

export const buildGlobalReportPlatformComparisonDataset = ({
  campaigns = {},
  statistics = {},
}) => {
  const campaignsStats = statistics?.campaigns || {};
  const byPlatform = {};

  Object.keys(campaigns).forEach((campaignID) => {
    const campaign = campaigns[campaignID];
    if (campaign?.states?.general === ADS_CAMPAIGN_STATUS_DRAFT) return;

    const unifiedCampaign = campaignsStats[campaignID];
    if (!unifiedCampaign) return;

    (unifiedCampaign.platforms || []).forEach((platform) => {
      const metrics =
        unifiedCampaign?.levels?.campaign?.maximum?.[platform]?.campaign;
      if (!metrics) return;

      if (!byPlatform[platform]) {
        byPlatform[platform] = {
          spendByCurrency: {},
          mainAction: 0,
        };
      }

      const currency = metrics.currency || metrics.account_currency || "USD";
      const spend = Number(metrics.spend || 0);
      if (spend > 0) {
        byPlatform[platform].spendByCurrency[currency] =
          (Number(byPlatform[platform].spendByCurrency[currency]) || 0) + spend;
      }
      byPlatform[platform].mainAction += Number(metrics.mainAction || 0);
    });
  });

  const portfolioSpendByCurrency = Object.values(byPlatform).reduce(
    (acc, row) => {
      Object.entries(row.spendByCurrency).forEach(([currency, amount]) => {
        acc[currency] = (Number(acc[currency]) || 0) + amount;
      });
      return acc;
    },
    {}
  );
  const primaryCurrency = getPrimarySpendCurrency(portfolioSpendByCurrency);

  const platforms = Object.keys(byPlatform).sort();
  const platformRows = platforms.map((platform) => {
    const row = byPlatform[platform];
    const currency =
      getPrimarySpendCurrency(row.spendByCurrency) || primaryCurrency || "USD";
    const spend = sumSpendFromMap(row.spendByCurrency, currency);
    const mainAction = Number(row.mainAction) || 0;

    return {
      label: platform,
      platform,
      spend,
      mainAction,
      currency,
    };
  });

  const activePlatformRows = platformRows.filter(
    (row) => hasPositiveNumber(row.spend) || hasPositiveNumber(row.mainAction)
  );

  const barChartData = platformRows
    .filter((row) => hasPositiveNumber(row.mainAction))
    .map((row, index) => ({
      label: row.platform,
      platform: row.platform,
      value: row.mainAction,
      fill: getGlobalReportPlatformColor(row.platform, index),
    }));

  const cpaBarChartData = platformRows
    .map((row, index) => {
      const cpa =
        row.mainAction > 0 && row.spend > 0 ? row.spend / row.mainAction : null;
      if (!hasPositiveNumber(cpa)) return null;

      return {
        label: row.platform,
        platform: row.platform,
        value: cpa,
        currency: row.currency,
        fill: getGlobalReportPlatformColor(row.platform, index),
      };
    })
    .filter(Boolean);

  const pieChartData = activePlatformRows
    .filter((row) => row.spend > 0)
    .map((row) => ({
      name: row.platform,
      label: row.platform,
      platform: row.platform,
      value: row.spend,
      currency: row.currency,
    }));

  const pieChartConfig = activePlatformRows.reduce((acc, row, index) => {
    acc[row.platform] = {
      label: row.platform,
      color: getGlobalReportPlatformColor(row.platform, index),
    };
    return acc;
  }, {});

  return {
    barChartData,
    barChartConfig: {
      value: {
        label: "Results",
        color: GLOBAL_REPORT_BAR_COLOR,
      },
    },
    cpaBarChartData,
    cpaBarChartConfig: {
      value: {
        label: "CPA",
        color: GLOBAL_REPORT_BAR_COLOR,
      },
    },
    pieChartData,
    pieChartConfig,
    primaryCurrency,
    activePlatformCount: activePlatformRows.length,
  };
};

const collectGlobalReportCreativeAds = ({
  campaigns = {},
  statistics = {},
  campaignDetails = {},
  resolveAdLabel,
  resolveAdMediaUrls,
}) => {
  const campaignsStats = statistics?.campaigns || {};
  const ads = [];

  Object.keys(campaigns).forEach((campaignID) => {
    const campaign = campaigns[campaignID];
    if (campaign?.states?.general === ADS_CAMPAIGN_STATUS_DRAFT) return;

    const unifiedCampaign = campaignsStats[campaignID];
    if (!unifiedCampaign) return;

    const detailCampaign = campaignDetails[campaignID] || campaign;
    const campaignName = getCampaignDisplayName(detailCampaign);

    (unifiedCampaign.platforms || []).forEach((platform) => {
      const platformAds = unifiedCampaign?.levels?.ads?.maximum?.[platform];
      if (!platformAds) return;

      Object.keys(platformAds).forEach((adID) => {
        const metrics = platformAds[adID];
        if (!metrics) return;

        const mainAction = Number(metrics.mainAction || 0);
        const spend = Number(metrics.spend || 0);
        const costPerMainAction = Number(metrics.costPerMainAction || 0);
        const cpa =
          mainAction > 0
            ? costPerMainAction > 0
              ? costPerMainAction
              : spend / mainAction
            : null;

        const hasResults = mainAction > 0 && cpa != null && cpa > 0;
        if (!hasResults) {
          return;
        }

        const label =
          (typeof resolveAdLabel === "function" &&
            resolveAdLabel({
              campaignID,
              platform,
              adID,
              campaign: detailCampaign,
              campaignName,
            })) ||
          `${campaignName} · ${adID}`;

        ads.push({
          adID,
          campaignID,
          platform,
          campaignName,
          label,
          cpa,
          mainAction,
          mediaUrls:
            (typeof resolveAdMediaUrls === "function" &&
              resolveAdMediaUrls({
                campaignID,
                adID,
                campaign: detailCampaign,
              })) ||
            [],
        });
      });
    });
  });

  return ads;
};

const toCreativeBarChartData = (ads, { getValue, sort }) => {
  const ranked = [...ads].sort(sort);

  return ranked.map((ad) => ({
    label: ad.label,
    value: getValue(ad),
    fill: GLOBAL_REPORT_BAR_COLOR,
    adID: ad.adID,
    campaignID: ad.campaignID,
    platform: ad.platform,
    campaignName: ad.campaignName,
    mediaUrls: ad.mediaUrls,
    cpa: ad.cpa,
    mainAction: ad.mainAction,
  }));
};

export const buildGlobalReportCreativeRankingDatasets = (params) => {
  const ads = collectGlobalReportCreativeAds(params);

  const lowestCpaChartData = toCreativeBarChartData(ads, {
    getValue: (ad) => ad.cpa,
    sort: (a, b) => a.cpa - b.cpa,
  });

  const highestVolumeChartData = toCreativeBarChartData(ads, {
    getValue: (ad) => ad.mainAction,
    sort: (a, b) => b.mainAction - a.mainAction,
  });

  return {
    lowestCpa: {
      chartData: lowestCpaChartData,
      chartConfig: {
        value: {
          label: "CPA",
          color: GLOBAL_REPORT_BAR_COLOR,
        },
      },
    },
    highestVolume: {
      chartData: highestVolumeChartData,
      chartConfig: {
        value: {
          label: "Results",
          color: GLOBAL_REPORT_BAR_COLOR,
        },
      },
    },
  };
};

/** @deprecated Use buildGlobalReportCreativeRankingDatasets */
export const buildGlobalReportCreativeRankingDataset = (params) =>
  buildGlobalReportCreativeRankingDatasets(params).lowestCpa;

export const buildGlobalReportCampaignRadarDataset = ({
  campaigns = {},
  statistics = {},
  metricLabels = {},
}) => {
  const campaignsStats = statistics?.campaigns || {};
  const campaignRows = [];

  Object.keys(campaigns).forEach((campaignID) => {
    const campaign = campaigns[campaignID];
    if (campaign?.states?.general === ADS_CAMPAIGN_STATUS_DRAFT) return;

    const unifiedCampaign = campaignsStats[campaignID];
    if (!unifiedCampaign) return;

    const metrics = aggregateCampaignMetrics(unifiedCampaign);
    const row = {
      campaignID,
      name: getCampaignDisplayName(campaign),
      ...metrics,
    };

    if (campaignHasChartActivity(row)) {
      campaignRows.push(row);
    }
  });

  if (!campaignRows.length) {
    return { chartData: [], chartConfig: {} };
  }

  const rawByMetric = RADAR_METRICS.reduce((acc, { key }) => {
    acc[key] = campaignRows.map((row) => row[key]);
    return acc;
  }, {});

  const normalizedByCampaign = campaignRows.map((row, rowIndex) => {
    const normalized = {};
    RADAR_METRICS.forEach(({ key, invert }) => {
      const series = normalizeSeries(rawByMetric[key], { invert });
      normalized[key] = series[rowIndex];
    });
    return normalized;
  });

  const chartData = RADAR_METRICS.map(({ key, labelKey }) => {
    const point = {
      metric: metricLabels[labelKey] || labelKey,
    };
    campaignRows.forEach((row, index) => {
      point[row.campaignID] = normalizedByCampaign[index][key];
    });
    return point;
  });

  const chartConfig = campaignRows.reduce((acc, row, index) => {
    acc[row.campaignID] = {
      label: row.name,
      color: getGlobalReportPaletteColor(index),
    };
    return acc;
  }, {});

  return { chartData, chartConfig, campaignRows };
};
